import { Pipe, PipeTransform } from '@angular/core';

/**
 * @param seconds - number of seconds difference between two dates
 * @param options - optional settings for long format and suffix inclusion
 * TimeDiffPipe returns how much time is left until reaching a date, or how much time as passed
 *
 *  use it with options to get long format or suffix:
 *    {{ seconds | timeDiff: { longFormat: true, includeSuffix: true } }}
 *
 * @returns output examples:
 *  - time in the future:
 *    - long: 2 hours 30 minutes left
 *    - short: 2h 30m left
 *  - time in the past:
 *    - long: 1 month 2 weeks ago
 *    - short: 2h 30m left
 */
@Pipe({ name: 'timeDiff' })
export class TimeDiffPipe implements PipeTransform {
  transform(seconds: number, options?: { longFormat?: boolean; includeSuffix?: boolean }): string {
    if (seconds) {
      const sign = Math.sign(seconds);
      let suffix = 'left'; // if the time is yet to come.
      if (sign === -1) {
        seconds = Math.floor(seconds * sign); // removing the sign and the float part -25.5  = 25 seconds
        suffix = 'ago'; // if time is already expired.
      }
      const intervals: Record<string, number> = {
        year: 31536000,
        Y: 31536000,
        month: 2592000,
        M: 2592000,
        week: 604800,
        w: 604800,
        day: 86400,
        d: 86400,
        hour: 3600,
        h: 3600,
        minute: 60,
        m: 60,
        second: 1,
        s: 1,
      };
      const allInterval = options?.longFormat
        ? ['year', 'month', 'week', 'day', 'hour', 'minute', 'second']
        : ['Y', 'M', 'w', 'd', 'h', 'm', 's'];
      let counter;
      for (let i of allInterval) {
        counter = Math.floor(seconds / intervals[i]);
        let toReturn = '';
        if (counter > 0) {
          toReturn += this.calculateTime(counter, i, !!options?.longFormat); // this will give 2 hours
          const timeLeft = seconds - counter * intervals[i]; // 3060 second
          const index = allInterval.indexOf(i) + 1; // get the index of next unit
          i = allInterval[index]; // value of next unit = minute
          if (index > 6) return toReturn + (options?.includeSuffix ? suffix : ''); // second ago for boundary case

          counter = Math.floor(timeLeft / intervals[i]); // 3060 second = 51
          toReturn =
            toReturn +
            ' ' +
            this.calculateTime(counter, i, !!options?.longFormat) +
            (options?.includeSuffix ? ` ${suffix}` : ''); // will calculate "2 hours 51 Minutes" from current time
          return toReturn;
        }
      }
    }
    return 'unknown';
  }

  calculateTime(counter: number, timeUnit: string, longFormat: boolean) {
    if (!longFormat) return counter + timeUnit; // short format like 2h, 30m

    if (counter === 1)
      return counter + ' ' + timeUnit; // singular (1 hour ago)
    else {
      const toReturn = counter + ' ' + timeUnit + 's ';
      return toReturn; // plural (2 hours ago)
    }
  }
}
